import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';

// Single "Date Range" control: the page's named ranges plus a custom start → end pick.
// customDates is { startDate, endDate } as 'yyyy-MM-dd' strings, both '' when a named range is in use.
export const NO_CUSTOM_DATES = { startDate: '', endDate: '' };

const DateRangeFilter = ({ ranges, range, customDates, onChange, disabled, note }) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(customDates);
    const isCustom = Boolean(customDates.startDate);
    const reversed = draft.startDate && draft.endDate && draft.startDate > draft.endDate;
    const today = format(new Date(), 'yyyy-MM-dd');
    const label = isCustom
        ? `${format(parseISO(customDates.startDate), 'dd MMM yy')} – ${format(parseISO(customDates.endDate), 'dd MMM yy')}`
        : range;

    const pickRange = (r) => { onChange(r, NO_CUSTOM_DATES); setOpen(false); };
    const applyCustom = () => { onChange(range, draft); setOpen(false); };

    return (
        <div className="relative">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Date Range</label>
            <button
                type="button"
                disabled={disabled}
                onClick={() => { setDraft(customDates); setOpen((o) => !o); }}
                className={`w-full p-2.5 bg-slate-50 border rounded-xl flex items-center gap-2 text-left font-medium text-slate-700 outline-none disabled:opacity-50 disabled:cursor-not-allowed ${open ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200'}`}
            >
                <Calendar className="w-4 h-4 text-blue-500 shrink-0" />
                <span className="flex-1 min-w-0 truncate text-sm">{label}</span>
                <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
            </button>
            {note && <p className="text-xs text-slate-400 mt-1.5 ml-1">{note}</p>}

            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute z-50 left-0 top-full mt-2 w-[min(32rem,calc(100vw-2rem))] bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden">
                        <div className="flex flex-col sm:flex-row">
                            {/* Named ranges */}
                            <div className="sm:w-44 shrink-0 p-2 bg-slate-50 border-b sm:border-b-0 sm:border-r border-slate-100">
                                <p className="px-2 pt-1 pb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Quick ranges</p>
                                <div className="grid grid-cols-2 sm:grid-cols-1 gap-1">
                                    {ranges.map((r) => {
                                        const active = !isCustom && r === range;
                                        return (
                                            <button
                                                key={r}
                                                type="button"
                                                onClick={() => pickRange(r)}
                                                className={`px-3 py-2 rounded-lg text-left text-sm font-medium transition-colors ${active ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-white hover:text-blue-600'}`}
                                            >
                                                {r}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Custom start → end */}
                            <div className="flex-1 min-w-0 p-4">
                                <p className="pb-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Custom range</p>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
                                        <input type="date" value={draft.startDate} max={today} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} className="w-full p-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
                                        <input type="date" value={draft.endDate} min={draft.startDate || undefined} max={today} onChange={(e) => setDraft({ ...draft, endDate: e.target.value })} className="w-full p-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                                    </div>
                                </div>
                                {reversed && <p className="text-xs text-red-500 mt-2">End date must be on or after the start date.</p>}
                                <div className="flex gap-2 mt-4">
                                    <button type="button" onClick={() => setOpen(false)} className="flex-1 px-3 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 text-sm font-medium">Cancel</button>
                                    <button
                                        type="button"
                                        disabled={!draft.startDate || !draft.endDate || reversed}
                                        onClick={applyCustom}
                                        className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default DateRangeFilter;
